import { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import type { ConversationTurn, LanguageItem } from '../../api/types';
import { parseTurnMetadata, extractSessionTargetItems, aggregateMetadata } from '../../lib/metadata-utils';

interface SessionInsightsProps {
  turns: ConversationTurn[];
  cardDifficulty: number;
  itemsMap?: Map<string, LanguageItem>;
}

export function SessionInsights({ turns, cardDifficulty, itemsMap }: SessionInsightsProps) {
  const agg = useMemo(() => aggregateMetadata(turns), [turns]);
  const targetItems = useMemo(() => extractSessionTargetItems(turns), [turns]);

  if (!agg.hasMetadata) {
    return (
      <div className="text-center text-gray-400 py-12">
        No training dynamics data available for this session.
      </div>
    );
  }

  // Build difficulty + comprehension curve data
  const curveData = useMemo(() => {
    let currentDifficulty = cardDifficulty;
    return turns.map(turn => {
      const meta = parseTurnMetadata(turn);
      if (meta?.difficulty_adjustment === 'up') currentDifficulty++;
      if (meta?.difficulty_adjustment === 'down') currentDifficulty--;
      const comprehension = meta?.comprehension_level
        ? ({ low: 1, medium: 2, high: 3 } as Record<string, number>)[meta.comprehension_level]
        : null;
      return {
        turn: turn.turn_index,
        difficulty: currentDifficulty,
        comprehension: turn.role === 'user' ? comprehension : null,
      };
    });
  }, [turns, cardDifficulty]);

  // Build heatmap data
  const heatmapData = useMemo(() => {
    if (!targetItems.length) return null;
    const turnNumbers = turns.map(t => t.turn_index);
    const grid: Record<string, Set<number>> = {};
    for (const itemId of targetItems) grid[itemId] = new Set();
    for (const turn of turns) {
      const meta = parseTurnMetadata(turn);
      meta?.target_items_hit?.forEach(id => {
        if (grid[id]) grid[id].add(turn.turn_index);
      });
    }
    return { targetItems, turnNumbers, grid };
  }, [turns, targetItems]);

  return (
    <div className="space-y-6">
      {/* Summary Card */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-indigo-600">{Math.round(agg.hitRate * 100)}%</p>
          <p className="text-xs text-gray-500">Vocab Hit Rate</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-emerald-600">{agg.newWordsIntroduced.length}</p>
          <p className="text-xs text-gray-500">New Words ({agg.newWordsUnderstoodCount} understood)</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-orange-600">
            {agg.difficultyEvents.filter(e => e.direction === 'up').length}↑ {agg.difficultyEvents.filter(e => e.direction === 'down').length}↓
          </p>
          <p className="text-xs text-gray-500">Difficulty Adj.</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-blue-600">
            {agg.comprehensionLevels.length > 0
              ? (agg.comprehensionLevels.reduce((sum, c) => sum + ({ low: 1, medium: 2, high: 3 } as Record<string, number>)[c.level], 0) / agg.comprehensionLevels.length).toFixed(1)
              : '—'}
          </p>
          <p className="text-xs text-gray-500">Avg Comprehension</p>
        </div>
      </div>

      {/* Difficulty & Comprehension Curve */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-2">Difficulty & Comprehension Curve</h4>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={curveData}>
            <XAxis dataKey="turn" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="difficulty" stroke="#6366f1" strokeWidth={2} name="Difficulty" dot={{ r: 3 }} connectNulls />
            <Line type="monotone" dataKey="comprehension" stroke="#10b981" strokeWidth={2} name="Comprehension" dot={{ r: 3 }} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Vocabulary Hit Heatmap */}
      {heatmapData && heatmapData.targetItems.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-2">Vocabulary Hit Map</h4>
          <div className="overflow-x-auto">
            <div className="inline-grid gap-0.5" style={{ gridTemplateColumns: `120px repeat(${heatmapData.turnNumbers.length}, 24px)` }}>
              {/* Header row */}
              <div className="text-xs text-gray-400" />
              {heatmapData.turnNumbers.map(t => (
                <div key={t} className="text-xs text-gray-400 text-center">{t}</div>
              ))}
              {/* Data rows */}
              {heatmapData.targetItems.map(itemId => (
                <div key={itemId} style={{ display: 'contents' }}>
                  <div className="text-xs text-gray-600 truncate pr-1">{itemsMap?.get(itemId)?.text ?? itemId.slice(0, 8)}</div>
                  {heatmapData.turnNumbers.map(t => (
                    <div key={`${itemId}-${t}`}
                         className={`w-5 h-5 rounded-sm ${heatmapData.grid[itemId].has(t) ? 'bg-indigo-500' : 'bg-gray-100'}`} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
